// User identity chip + settings entry point.
// Used in two layouts:
//   - "compact" (mobile header right side) — avatar + settings button
//   - "row" (desktop sidebar bottom) — avatar + first name + settings (IMP-042)
//
// The avatar (IMP-042) shows the user's directory photo when one is available,
// falling back to initials-on-processBlue, and opens the user's own Business
// Card on click. Photo + card data come from the user's own directory record.

import { lazy, Suspense, useState } from 'react';
import clsx from 'clsx';
import { Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/auth';
import { useDirectoryEntry } from '../lib/directory';
import type { Capability } from '@onlogic/shared';
import { CAP } from '@onlogic/shared';

// The admin-type capabilities that earn the "Admin" subheading. A typed set
// (instead of the old `includes('admin')` string match) so a capability
// rename is a compile error here rather than a silently wrong label.
const ADMIN_CAPS: ReadonlySet<Capability> = new Set<Capability>([
  CAP.systemAdmin,
  CAP.securityAdmin,
  CAP.facilitiesAdmin,
  CAP.contentAdmin,
  CAP.fleetAdmin,
  CAP.eventsAdmin,
  CAP.surveyAdmin,
  CAP.flightAdmin,
]);

// Lazy so the business-card code (and its QR-code dependency) stays out of the
// always-mounted app-shell chunk and only loads when the card is opened.
const BusinessCardModal = lazy(() =>
  import('./BusinessCardModal').then((m) => ({ default: m.BusinessCardModal })),
);

interface UserMenuProps {
  variant: 'compact' | 'row';
  className?: string;
}

export function UserMenu({ variant, className }: UserMenuProps) {
  const { user, claims } = useAuth();
  const [cardOpen, setCardOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  // The signed-in user's own directory record — supplies the avatar photo and
  // the Business Card contents. The same hook as the Home page's, so the read
  // is shared through TanStack Query's cache. Purely cosmetic: the initials
  // fallback renders immediately, so this never blocks the shell.
  const { data: myEntry } = useDirectoryEntry(user?.uid);

  if (!user) return null;

  const initials =
    user.displayName
      ?.split(/\s+/)
      .map((n) => n.charAt(0))
      .slice(0, 2)
      .join('') ?? '?';

  const photoUrl = myEntry?.avatarUrl;
  const showPhoto = !!photoUrl && !imgError;
  const tooltip = user.displayName ?? user.email ?? '';
  const fullName = user.displayName ?? 'Signed in';

  // "Admin" subheading for anyone holding an admin-type capability (see
  // ADMIN_CAPS above); everyone else sees "OnLogic". Cosmetic only — never an
  // authorization gate (rule #6); the actual checks are server-side.
  const isAdmin = (claims?.capabilities ?? []).some((c) => ADMIN_CAPS.has(c));

  // The colored circle stays small (32 / 36 px); the surrounding button adds
  // padding so the tap target clears 44 CSS px on mobile (IMP-042). Only the
  // inner circle carries the pill shape, so this adds no extra radius utility
  // to the audit ratchet.
  const avatar = (sizeClass: string) => (
    <button
      type="button"
      onClick={() => myEntry && setCardOpen(true)}
      title={tooltip}
      aria-label="Show my business card"
      className="flex shrink-0 items-center justify-center p-1.5 transition-opacity hover:opacity-80"
    >
      <span
        className={clsx(
          'flex items-center justify-center overflow-hidden rounded-full bg-processBlue text-xs font-medium text-white',
          sizeClass,
        )}
      >
        {showPhoto ? (
          <img
            src={photoUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          initials.toUpperCase()
        )}
      </span>
    </button>
  );

  return (
    <>
      {variant === 'compact' ? (
        <div className={clsx('flex items-center gap-1', className)}>
          {avatar('h-8 w-8')}
          <Link
            to="/settings"
            className="rounded-md p-2 text-steel transition-colors hover:bg-n-100 hover:text-carbon"
            aria-label="Settings"
          >
            <Settings size={18} />
          </Link>
        </div>
      ) : (
        // row variant — desktop sidebar bottom. Avatar + name block + gear. The
        // name block shows the full "First Last" plus an Admin/OnLogic subheading,
        // and (like the avatar) opens the user's Business Card on click. The name
        // sits tight to the avatar (-ml-0.5 + small gap) per the IMP-049 tweak.
        // Reduced side padding (px-0.5) nudges the avatar/name toward the left
        // edge and the cog toward the right, widening the middle so longer
        // "First Last" names fit before truncating. Name + subheading are
        // intentionally light (not bold).
        <div className={clsx('flex items-center gap-1 px-0.5 py-2', className)}>
          {avatar('h-9 w-9')}
          <button
            type="button"
            onClick={() => myEntry && setCardOpen(true)}
            title={tooltip}
            aria-label="Show my business card"
            className="-ml-0.5 min-w-0 flex-1 text-left leading-tight transition-opacity hover:opacity-80"
          >
            <span className="block truncate text-sm font-normal text-carbon">{fullName}</span>
            <span className="block truncate text-overline uppercase tracking-wider text-steel">
              {isAdmin ? 'Admin' : 'OnLogic'}
            </span>
          </button>
          <Link
            to="/settings"
            className="shrink-0 rounded-md p-2 text-steel transition-colors hover:bg-n-100 hover:text-carbon"
            aria-label="Settings"
          >
            <Settings size={20} />
          </Link>
        </div>
      )}

      {cardOpen && myEntry && (
        <Suspense fallback={null}>
          <BusinessCardModal user={myEntry} onClose={() => setCardOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
